import { motion } from 'framer-motion';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Map from '../../centered/components/Map';
import { getPaymentUrl, getPortalUrl } from '../../../utils/domainUtils';
import { useResponsive } from '../../../shared/hooks/useResponsive';

interface ImmersiveBookingConfirmationProps {
  booking: any;
  selectedServices: any[];
  selectedDateTime: Date;
  customerInfo: any;
  widgetConfiguration: any;
  location: any;
  timeZone: string;
  serviceProviderMap: any;
  nodeData?: any;
}

function cloudinaryFill(url: string, w: number, h: number): string {
  if (!url) return url;
  const idx = url.indexOf('/upload/');
  if (idx === -1) return url;
  return url.slice(0, idx + 8) + `c_fill,g_auto,w_${w},h_${h}/` + url.slice(idx + 8);
}

const PANEL_W = 960;
const PANEL_H = 1080;

const ImmersiveBookingConfirmation: React.FC<ImmersiveBookingConfirmationProps> = ({
  booking,
  selectedServices,
  selectedDateTime,
  customerInfo,
  widgetConfiguration,
  location,
  timeZone,
  serviceProviderMap,
  nodeData,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const currency = widgetConfiguration?.currency?.symbol ?? '$';
  const isPaymentRequired = widgetConfiguration?.isPaymentActivated && widgetConfiguration?.isPaymentMandatory;
  const isDepositRequired = booking?.isDepositRequired;
  const showPayment = nodeData?.settings?.enablePayment ?? true;

  const hasProviders = serviceProviderMap && Object.keys(serviceProviderMap).length > 0;
  const hasLocation = location?.latitude && location?.longitude;
  const multiService = selectedServices?.length > 1;

  const firstService = selectedServices?.[0];
  const firstProvider = firstService ? serviceProviderMap?.[firstService.id] : null;
  const providerName = firstProvider
    ? ('autoAssigned' in firstProvider ? 'Auto-assigned' : `${firstProvider.firstName ?? ''} ${firstProvider.lastName ?? ''}`.trim())
    : null;

  const serviceImageUrl = firstService?.image?.secure_url
    ? cloudinaryFill(firstService.image.secure_url, PANEL_W, PANEL_H)
    : null;
  const providerImageUrl = firstProvider?.image?.secure_url
    ? cloudinaryFill(firstProvider.image.secure_url, PANEL_W, PANEL_H)
    : null;

  const formattedDate = selectedDateTime
    ? moment(selectedDateTime).format('ddd, D MMM YYYY')
    : '—';
  const formattedTime = booking?.start
    ? moment(booking.start).format('HH:mm')
    : '—';

  // panels array for animation delay calculation
  const panels: string[] = ['service'];
  if (hasProviders && !multiService) panels.push('provider');
  if (hasLocation) panels.push('location');
  panels.push('details');

  const copyReference = () => {
    if (booking?.reference) navigator.clipboard.writeText(booking.reference);
  };

  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden', background: '#080808', position: 'relative' }}>
        <style>{`#lp-booked-m::-webkit-scrollbar { display: none; }`}</style>


        <div id="lp-booked-m" style={{ height: 'calc(100vh - 72px)', overflowY: 'auto', scrollbarWidth: 'none' }}>

          {/* Service card — confirmed badge */}
          <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            {serviceImageUrl
              ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${serviceImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)' }} />}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
            <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <div style={{ width: 15, height: 15, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckIcon />
                </div>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', margin: 0 }}>Confirmed</p>
              </div>
              {multiService ? (
                <>
                  <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{selectedServices.length} services</p>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>{selectedServices.map(s => s.name).join(' · ')}</p>
                </>
              ) : (
                <>
                  <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{firstService?.name}</p>
                  {firstService?.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>{Math.round(firstService.duration / 60)} min</p>}
                </>
              )}
            </div>
          </div>

          {/* Provider card */}
          {hasProviders && !multiService && (
            <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              {providerImageUrl
                ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${providerImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)' }} />}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Provider</p>
                {providerName && <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{providerName}</p>}
              </div>
            </div>
          )}

          {/* Location card */}
          {hasLocation && (
            <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <Map position={{ lat: location.latitude, lng: location.longitude }} onLocationChange={() => {}} disableInteraction zoom={14} showMarker />
              </div>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Location</p>
                <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{location.name}</p>
                {location.address && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>{location.address}</p>}
              </div>
            </div>
          )}

          {/* Details */}
          <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>Your booking</p>
            <p style={{ color: '#fff', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {customerInfo?.firstName} {customerInfo?.lastName}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: 13, letterSpacing: '0.04em', lineHeight: 1.7 }}>
              {customerInfo?.email}{customerInfo?.phone && <><br />{customerInfo.phone}</>}
            </p>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <DetailRow label="Date" value={formattedDate} />
              <DetailRow label="Time" value={`${formattedTime} · ${timeZone}`} />
              {booking?.amount !== undefined && <DetailRow label="Total" value={`${currency}${Number(booking.amount).toFixed(2)}`} />}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, zIndex: 10 }}>
          {booking?.reference && (
            <button onClick={copyReference} title={t('confirmation.copyBookingId')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: 'rgba(255,255,255,0.45)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0 }}>
              <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>#</span>
              {booking.reference}
              <CopyIcon />
            </button>
          )}
          <div style={{ flex: 1 }} />
          {showPayment && isPaymentRequired && (
            <motion.button
              onClick={() => { window.location.href = getPaymentUrl(booking.id); }}
              whileTap={{ scale: 0.97 }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 2, color: '#fff', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0 }}>
              {t('confirmation.payNow')}
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>→</span>
            </motion.button>
          )}
          <a href={getPortalUrl()} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', padding: '11px 16px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box', flexShrink: 0 }}>
            {t('confirmation.dashboard')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      background: '#080808',
    }}>

      {/* ── Service panel ── */}
      <Panel imageUrl={serviceImageUrl} index={0}>
        {/* Confirmed badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <div style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <CheckIcon />
          </div>
          <PanelLabel style={{ marginBottom: 0 }}>Confirmed</PanelLabel>
        </div>

        {multiService ? (
          <>
            <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {selectedServices.length} services
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.04em', marginTop: 4, lineHeight: 1.5 }}>
              {selectedServices.map(s => s.name).join(' · ')}
            </p>
          </>
        ) : (
          <>
            <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {firstService?.name}
            </p>
            {firstService?.duration && (
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', marginTop: 4 }}>
                {Math.round(firstService.duration / 60)} min
              </p>
            )}
          </>
        )}
      </Panel>

      {/* ── Provider panel (single service only) ── */}
      {hasProviders && !multiService && (
        <>
          <Divider />
          <Panel imageUrl={providerImageUrl} index={1}>
            <PanelLabel>Provider</PanelLabel>
            {providerName && (
              <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {providerName}
              </p>
            )}
          </Panel>
        </>
      )}

      {/* ── Location panel ── */}
      {hasLocation && (
        <>
          <Divider />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: panels.indexOf('location') * 0.08, ease: [0.23, 1, 0.32, 1] }}
            style={{ flex: 1, position: 'relative', height: '100%', overflow: 'hidden' }}
          >
            <div style={{ position: 'absolute', inset: 0 }}>
              <Map
                position={{ lat: location.latitude, lng: location.longitude }}
                onLocationChange={() => {}}
                disableInteraction
                zoom={14}
                showMarker
              />
            </div>
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.85) 100%)',
            }} />
            <div style={{ position: 'absolute', bottom: 88, left: 0, right: 0, padding: '0 28px' }}>
              <PanelLabel>Location</PanelLabel>
              <p style={{ color: '#fff', fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {location.name}
              </p>
              {location.address && (
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.04em', marginTop: 5 }}>
                  {location.address}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}

      {/* ── Details panel ── */}
      <Divider />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: (panels.length - 1) * 0.08, ease: [0.23, 1, 0.32, 1] }}
        style={{
          flex: 1,
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          background: 'linear-gradient(160deg, #0f0f0f 0%, #121212 100%)',
        }}
      >
        <div style={{
          position: 'absolute',
          top: 52,
          bottom: 96,
          left: 0,
          right: 0,
          padding: '0 28px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <PanelLabel>Your booking</PanelLabel>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <p style={{
              color: '#ffffff',
              fontSize: 'clamp(36px, 3.6vw, 56px)',
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              marginBottom: 16,
              textAlign: 'center',
            }}>
              {customerInfo?.firstName}<br />{customerInfo?.lastName}
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.32)',
              fontSize: 13,
              letterSpacing: '0.04em',
              lineHeight: 1.7,
              textAlign: 'center',
            }}>
              {customerInfo?.email}
              {customerInfo?.phone && <><br />{customerInfo.phone}</>}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 4 }} />
            <DetailRow label="Date" value={formattedDate} />
            <DetailRow label="Time" value={`${formattedTime} · ${timeZone}`} />
            {booking?.amount !== undefined && (
              <DetailRow label="Total" value={`${currency}${Number(booking.amount).toFixed(2)}`} />
            )}
          </div>
        </div>
      </motion.div>

      {/* ── Bottom bar ── */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 80,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 48px',
        gap: 24,
        zIndex: 10,
      }}>

        {/* Reference pill */}
        {booking?.reference && (
          <button
            onClick={copyReference}
            title={t('confirmation.copyBookingId')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 14px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6,
              color: 'rgba(255,255,255,0.45)',
              fontSize: 12,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>#</span>
            {booking.reference}
            <CopyIcon />
          </button>
        )}

        {/* Payment warning */}
        {(isPaymentRequired || isDepositRequired) && showPayment && (
          <span style={{ color: 'rgba(255,100,100,0.8)', fontSize: 11, letterSpacing: '0.04em', flexShrink: 0 }}>
            {isDepositRequired ? t('confirmation.depositRequired') : t('confirmation.completePaymentNotice')}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Pay now */}
        {showPayment && isPaymentRequired && (
          <motion.button
            onClick={() => { window.location.href = getPaymentUrl(booking.id); }}
            whileHover={{ background: 'rgba(255,255,255,0.14)', scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 28px',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 2,
              color: '#fff',
              fontSize: 13,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t('confirmation.payNow')}
            <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)' }}>→</span>
          </motion.button>
        )}

        {/* Dashboard */}
        <a
          href={getPortalUrl()}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 24px',
            background: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            color: 'rgba(255,255,255,0.45)',
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            textDecoration: 'none',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          {t('confirmation.dashboard')}
        </a>
      </div>
    </div>
  );
};

// ── Sub-components (mirror ImmersiveConfirmationPanels) ───────────────────────

const Panel: React.FC<{
  imageUrl: string | null;
  index: number;
  children: React.ReactNode;
}> = ({ imageUrl, index, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: [0.23, 1, 0.32, 1] }}
    style={{ flex: 1, position: 'relative', height: '100%', overflow: 'hidden' }}
  >
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: imageUrl
        ? `url(${imageUrl})`
        : 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }} />
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.92) 100%)',
    }} />
    <div style={{ position: 'absolute', bottom: 88, left: 0, right: 0, padding: '0 28px' }}>
      {children}
    </div>
  </motion.div>
);

const Divider: React.FC = () => (
  <div style={{ width: 1, flexShrink: 0, background: 'rgba(255,255,255,0.07)', zIndex: 2 }} />
);

const PanelLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <p style={{
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    marginBottom: 12,
    ...style,
  }}>
    {children}
  </p>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2 }}>
      {label}
    </p>
    <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, letterSpacing: '0.02em', lineHeight: 1.4 }}>
      {value}
    </p>
  </div>
);

const CheckIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
    <path d="M4 10l4.5 4.5L16 6" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CopyIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="4" y="4" width="7" height="7" rx="1" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
    <path d="M2 8V2h6" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

export default ImmersiveBookingConfirmation;
